import { useCallback, useContext, useEffect, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { format, isValid } from 'date-fns';

import { HR } from '@zillow/zrs-comet';
import { Alert, Box, Tag, Text, TextButton } from '@zillow/constellation-10';

import {
  FeeType,
  useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_getFeesForType,
} from '@zg-rentals/rental-manager-env';

import { EditListingTourConfigModal, EditListingTourConfigSteps, type useTourCalendarApi } from '@zg-rentals/touring';

import { RMWContext } from '../../../../providers/RMWContext';
import { ModularListingContext } from '../../../../containers/CreateListingPage/context';

import { useSelector } from '../../../../selectors/useSelector';

import actions from '../../../../containers/CreateListingPage/actions';
import PropertiesActions from '../../../../actions/PropertiesActions';
import PostingActions from '../../../../actions/PostingActions';

import accessUtils from '../../../../utils/accessUtils';
import numberUtils from '../../../../utils/numberUtils';
import propertyUtils from '../../../../utils/propertyUtils';
import typeCheckUtils from '../../../../utils/typeCheckUtils';

import useAccessPostingPathTourScheduling from '../../../../hooks/useAccessPostingPathTourScheduling';

import { RECS, REVIEW_PAGE_SECTION_RECS } from '../../../../constants/RecommendationsConstants';
import { FOR_RENT_BY_KEY } from '../../../../containers/CreateListingPage/constants';
import { DEFAULT_VERIFICATION_CONFIG } from '../../../Modal/VerificationModal/constants';
import {
  ALLOW_CONTACT_BY_PHONE,
  FOR_RENT_BY,
  LEASE_DURATION,
  MODAL_COMPONENT,
  RECEIVE_ZILLOW_APPLICATIONS,
  RENTERS_INSURANCE,
} from '../constants';
import { HOUSING_CONNECTOR_NETWORK_OPTIONS } from '../../HousingConnector/constants';

import RecommendationsLearnMoreContent from '../../../RecommendationsLearnMoreContent';
import RecommendationIconBulb from '../../../RecommendationIconBulb';
import ContactEmailDeliverabilityAlert from '../../../ContactEmailDeliverabilityAlert';
import SchedulerFormField from '../../AvailableToShowProperty/components/SchedulerFormField';
import PhoneNumberDisplayInfo from '../../../PhoneNumberDisplayInfo';
import ContactEmailMismatchAlert from '../../../ContactEmailMismatchAlert';
import { BROKER_LICENSE_MAP } from '../../../AgentLicenseType';
import { VerificationModal } from '../../../Modal/VerificationModal';

import ApplicationsCSEvents from '../../../../model/ClickstreamEvent/ApplicationsCSEvents';
import TouringModularListingCSEvents from '../../../../model/ClickstreamEvent/Touring/TouringModularListingCSEvents';

import AccordionIconError from './AccordionIconError';
import ContentSectionEditButton from './ContentSectionEditButton';
import FieldValueDisplay from './FieldValueDisplay';
import TouringSection from './TouringSection';
import ApplicationSampleModal from '../../../Modal/ApplicationSampleModal';

import { StyledIconPlaceholder, StyledReviewSection, StyledReviewSectionTitle, StyledReviewTitleRow } from '../styles';

import type { MouseEvent } from 'react';

const { CONTACT } = REVIEW_PAGE_SECTION_RECS;

const NONE = 'None';

const ariaLabels = {
  CONTACT_BY_PHONE: 'contact-by-phone',
  CONTACT_INFO_EMAIL: 'contact-info-email',
  CONTACT_INFO_NAME: 'contact-info-name',
  CONTACT_INFO_PHONE: 'contact-info-phone',
  DATE_AVAILABLE: 'date-available',
  FOR_RENT_BY: 'for-rent-by',
  HOUSING_CONNECTOR: 'housing-connector',
  LEASE_LENGTH: 'lease-length',
  LEASE_TERMS: 'lease-terms',
  LISTED_BY: 'listed-by',
  RENTAL_PROTECTION: 'rental-protection',
  RENTERS_INSURANCE: 'renters-insurance',
  TOUR_AVAILABILITY: 'tour-availability',
  ZILLOW_APPLICATIONS: 'zillow-applications',
} as const;

type Props = {
  onEditClick: (e: MouseEvent<HTMLButtonElement>) => void;
  canAccessLightweightOptinExperiment?: boolean;
  tourCalendarHook: ReturnType<typeof useTourCalendarApi>;
};

const ContactContent = ({ onEditClick, tourCalendarHook }: Props) => {
  const dispatch = useDispatch();

  const { paths } = useContext(RMWContext);
  const {
    modularListingState: { canAccessSFCostsAndFees, isInReactivationFlow, isSaveComplete },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const {
    app: {
      appConfig,
      appConfig: {
        config: {
          features: { canAccessHousingConnector },
        },
      },
      cookieEnabledServices,
    },
    properties: {
      recommendations,
      currentProperty,
      currentProperty: {
        active,
        agentLicenseType,
        contactEmail,
        contactName,
        contactPhone,
        forRentBy,
        hidePhone,
        housingConnectorStatus,
        isApplicationsAccepted,
        isArchived,
        isFeed,
        isListingExclusive,
        leaseDuration,
        leaseTerms,
        listingAlias,
        floorplans: [{ dateAvailable: pDateAvailable = '' } = {}],
        rentersInsurance: { isEligibleForIsRentersInsuranceRequiredQuestion, isRentersInsuranceRequired },
        restrictions: {
          applications: {
            isUpdateRestricted: isApplicationsUpdateRestricted,
            isAccessRestricted: isApplicationsAccessRestricted,
            ownerEmail: applicationsOwnerEmail,
          },
          isUpdateAgentRestricted,
          isUpdateListingRestricted,
        },
        roomForRent: { hasPermissionToCreateListing },
      },
    },
    user,
    user: {
      features: { canUpdateListing, canViewContactEmailDeliverability },
      isHousingConnector,
    },
  } = useSelector((state) => state, shallowEqual);
  const { canBeRelisted } = useSelector((state) => ({ canBeRelisted: state.properties.currentProperty.canBeRelisted }));
  const canAccessRelistingFlow = accessUtils.canAccessRelistingFlow({ canBeRelisted });

  const [recommendationType, setRecommendationType] = useState('');
  const [showPhoneVerificationModal, setShowPhoneVerificationModal] = useState(false);
  const [displaySampleModal, setDisplaySampleModal] = useState(false);
  const [touringModalStep, setTouringModalStep] = useState<EditListingTourConfigSteps>(
    EditListingTourConfigSteps.SET_BOOKING_METHOD,
  );
  const [displayTourBookingModal, setDisplayTourBookingModal] = useState(false);

  const { data: applicationFee = [] } = useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_getFeesForType(
    {
      feeType: FeeType.APPLICATION_FEE,
      listingAlias,
    },
    {
      enabled: canAccessSFCostsAndFees && listingAlias !== '',
    },
  );

  const isLegacyTourDisabled = accessUtils.isLegacyTourDisabled({ appConfig, cookieEnabledServices });
  const { canAccessPostingPathTourScheduling } = useAccessPostingPathTourScheduling();

  const feeAmount = applicationFee?.length ? applicationFee?.[0]?.feeAmountMin : null;

  const customNoneFieldValueText = isFeed ? NONE : 'Action needed';

  const displayContactPhone = numberUtils.formatPhoneNumberInput(contactPhone);
  const displayLeaseDuration = LEASE_DURATION[leaseDuration];
  const displayRentersInsurance = RENTERS_INSURANCE[isRentersInsuranceRequired];

  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });

  const isDisabled = isFeed || !canUpdateListing || isArchived || isUpdateListingRestricted;

  const displayListingExclusive = isListingExclusive ? 'Yes' : 'No';

  // after format the day is behind by 1 day
  const dateAvailable = new Date(pDateAvailable);

  const displayDateAvailable =
    pDateAvailable &&
    isValid(dateAvailable) &&
    format(new Date(dateAvailable.getTime() - dateAvailable.getTimezoneOffset() * -60000), 'MM/dd/yyyy');

  const isForRentByTenantAndMissingPermissionToCreate =
    isRoomForRent && forRentBy === 'tenant' && hasPermissionToCreateListing === false;

  /*
   * NOTE:
   * > `isSaveComplete` is added to call `getRequiredFieldsErrors` inside `SFAccordionContainer`
   * > `.then` is added `saveContactPhone` in order to set `isSaveComplete` as `true`
   * > `useEffect` below sets `isSaveComplete` to `false`
   */
  useEffect(() => {
    if (!showPhoneVerificationModal && isSaveComplete) {
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
    }
  }, [showPhoneVerificationModal]);

  const trackTouringModalImpression = useCallback(
    (step: EditListingTourConfigSteps) => {
      if (
        step === EditListingTourConfigSteps.SET_BOOKING_METHOD ||
        step === EditListingTourConfigSteps.EDIT_BOOKING_METHOD
      ) {
        return TouringModularListingCSEvents.ReviewStepBookingMethodImpression({
          currentProperty,
          user,
          isActive: currentProperty.active,
        });
      }
      if (step === EditListingTourConfigSteps.EDIT_AVAILABILITY) {
        return TouringModularListingCSEvents.ReviewStepEditAvailabilityImpression({
          currentProperty,
          user,
          isActive: currentProperty.active,
        });
      }
    },
    [currentProperty, user],
  );

  const handleTouringModalStepChange = (step: EditListingTourConfigSteps) => {
    trackTouringModalImpression(step);
  };

  useEffect(() => {
    if (displayTourBookingModal) {
      trackTouringModalImpression(touringModalStep);
    }
  }, [displayTourBookingModal, touringModalStep, trackTouringModalImpression]);

  const saveContactPhone = (phoneNumber: string) =>
    dispatch(
      PostingActions.property.edit(
        {
          fieldName: 'contactPhone',
          value: phoneNumber,
          propertyId: listingAlias,
        },
        { reserveErrorObject: true },
      ),
    )
      // @ts-expect-error
      .then(
        ({ response }: { response: { dateVerified: string; dateVerifiedTimestamp: string; contactPhone: string } }) => {
          actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: true });

          return response;
        },
      );

  const handlePhoneNumberAdded = (cleanPhoneNumber: string) =>
    saveContactPhone(cleanPhoneNumber).then(() => {
      dispatch(PropertiesActions.updateCurrentProperty({ contactPhone: cleanPhoneNumber }));
    });

  const handleVerificationConfirmation = (phoneNumber: string) =>
    saveContactPhone(phoneNumber).then(
      (response: { dateVerified: string; dateVerifiedTimestamp: string; contactPhone: string }) => {
        dispatch(PropertiesActions.updateCurrentProperty({ contactPhone: response?.contactPhone || phoneNumber }));
      },
    );

  const handleEditPhoneClick = (event: MouseEvent<HTMLButtonElement>) => {
    setShowPhoneVerificationModal(true);
    onEditClick(event);
  };

  const handleEditTourClick = (modalStep: EditListingTourConfigSteps) => {
    if (
      modalStep === EditListingTourConfigSteps.EDIT_BOOKING_METHOD ||
      modalStep === EditListingTourConfigSteps.SET_BOOKING_METHOD
    ) {
      TouringModularListingCSEvents.ReviewStepEditBookingMethodClick({
        currentProperty,
        user,
        isActive: currentProperty.active,
      });
    } else if (modalStep === EditListingTourConfigSteps.EDIT_AVAILABILITY) {
      TouringModularListingCSEvents.ReviewStepEditAvailabilityClick({
        currentProperty,
        user,
        isActive: currentProperty.active,
      });
    }

    setTouringModalStep(modalStep);
    setDisplayTourBookingModal(true);
  };

  const handleTourDurationSave = useCallback(() => {
    TouringModularListingCSEvents.ReviewStepTourDurationClick({
      currentProperty,
      user,
      isActive: currentProperty.active,
      action: 'save',
    });
  }, [currentProperty, user]);

  const handleTourDurationCancel = useCallback(() => {
    TouringModularListingCSEvents.ReviewStepTourDurationClick({
      currentProperty,
      user,
      isActive: currentProperty.active,
      action: 'cancel',
    });
  }, [currentProperty, user]);

  return (
    <div data-testid="ContactContent" data-group-title="Contact">
      <StyledReviewSection data-testid="DateAvailable">
        <StyledReviewTitleRow>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconPlaceholder>
              {recommendations[CONTACT][RECS.DATE_AVAILABLE] && (
                <RecommendationIconBulb
                  fieldName={ariaLabels.DATE_AVAILABLE}
                  removeFlex={true}
                  onClick={() => {
                    setRecommendationType(RECS.DATE_AVAILABLE);
                  }}
                />
              )}
            </StyledIconPlaceholder>
            <StyledReviewSectionTitle>
              <Text textStyle="body-bold">Date available</Text>
              {recommendations[CONTACT][RECS.DATE_AVAILABLE] && (
                <TextButton
                  aria-label={`learn-more-about-${ariaLabels.DATE_AVAILABLE}`}
                  textStyle="body"
                  onClick={() => {
                    setRecommendationType(RECS.DATE_AVAILABLE);
                  }}
                >
                  Learn more
                </TextButton>
              )}
            </StyledReviewSectionTitle>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.DATE_AVAILABLE}
            value={MODAL_COMPONENT.DateAvailableFormField.componentKey}
            onEditClick={onEditClick}
          />
        </StyledReviewTitleRow>
        <FieldValueDisplay value={displayDateAvailable} />
      </StyledReviewSection>

      <StyledReviewSection data-testid="LeaseTerms">
        <StyledReviewTitleRow>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconPlaceholder>
              {recommendations[CONTACT][RECS.LEASE_TERMS] && (
                <RecommendationIconBulb
                  fieldName={ariaLabels.LEASE_TERMS}
                  removeFlex={true}
                  onClick={() => {
                    setRecommendationType(RECS.LEASE_TERMS);
                  }}
                />
              )}
            </StyledIconPlaceholder>
            <StyledReviewSectionTitle>
              <Text textStyle="body-bold">Lease terms</Text>
              <TextButton
                aria-label={`learn-more-about-${ariaLabels.LEASE_TERMS}`}
                textStyle="body"
                onClick={() => {
                  setRecommendationType(RECS.LEASE_TERMS);
                }}
              >
                Learn more
              </TextButton>
            </StyledReviewSectionTitle>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.LEASE_TERMS}
            value={MODAL_COMPONENT.LeaseTermsFormField.componentKey}
            onEditClick={onEditClick}
          />
        </StyledReviewTitleRow>
        <FieldValueDisplay value={leaseTerms} />
      </StyledReviewSection>

      {isEligibleForIsRentersInsuranceRequiredQuestion && (
        <StyledReviewSection data-testid="RentersInsurance">
          <StyledReviewTitleRow>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <StyledIconPlaceholder />
              <Text textStyle="body-bold">Renters Insurance</Text>
            </Box>

            <ContentSectionEditButton
              buttonFieldName={ariaLabels.RENTERS_INSURANCE}
              value={MODAL_COMPONENT.RentersInsuranceFieldSet.componentKey}
              onEditClick={onEditClick}
            />
          </StyledReviewTitleRow>
          <FieldValueDisplay value={displayRentersInsurance} />
        </StyledReviewSection>
      )}

      <StyledReviewSection data-testid="LeaseDuration">
        <StyledReviewTitleRow>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconPlaceholder>
              {!leaseDuration && !isDisabled && !recommendations[CONTACT][RECS.LEASE_LENGTH] && (
                <AccordionIconError tabIndex={0} title="Lease duration incomplete error" marginRight="xs" />
              )}
              {recommendations[CONTACT][RECS.LEASE_LENGTH] && (
                <RecommendationIconBulb
                  fieldName={ariaLabels.LEASE_LENGTH}
                  removeFlex={true}
                  onClick={() => {
                    setRecommendationType(RECS.LEASE_LENGTH);
                  }}
                />
              )}
            </StyledIconPlaceholder>
            <StyledReviewSectionTitle>
              <Text textStyle="body-bold">Lease duration</Text>
              <TextButton
                aria-label={`learn-more-about-${ariaLabels.LEASE_LENGTH}`}
                textStyle="body"
                onClick={() => {
                  setRecommendationType(RECS.LEASE_LENGTH);
                }}
              >
                Learn more
              </TextButton>
            </StyledReviewSectionTitle>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.LEASE_LENGTH}
            value={MODAL_COMPONENT.LeaseDurationFieldSet.componentKey}
            onEditClick={onEditClick}
          />
        </StyledReviewTitleRow>
        <FieldValueDisplay value={displayLeaseDuration} />
      </StyledReviewSection>

      <HR />
      <Box css={{ marginBottom: '1000' }} />

      {isRoomForRent ? (
        <StyledReviewSection data-testid="Contact">
          <StyledReviewTitleRow>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <StyledIconPlaceholder>
                {(!forRentBy || isForRentByTenantAndMissingPermissionToCreate) && !isDisabled && (
                  <AccordionIconError tabIndex={0} title="Listed by incomplete error" marginRight="xs" />
                )}
              </StyledIconPlaceholder>
              <Text textStyle="body-bold">Listed by</Text>
            </Box>

            <ContentSectionEditButton
              buttonFieldName={ariaLabels.LISTED_BY}
              value={MODAL_COMPONENT.RoomForRentByFieldSet.componentKey}
              onEditClick={onEditClick}
            />
          </StyledReviewTitleRow>

          <FieldValueDisplay value={FOR_RENT_BY[forRentBy]} />
        </StyledReviewSection>
      ) : (
        <StyledReviewSection data-testid="Contact">
          <StyledReviewTitleRow>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <StyledIconPlaceholder>
                {!forRentBy && !isDisabled && (
                  <AccordionIconError tabIndex={0} title="Listed by incomplete error" marginRight="xs" />
                )}
              </StyledIconPlaceholder>
              <Text textStyle="body-bold">Listed by</Text>
            </Box>

            <ContentSectionEditButton
              buttonFieldName={ariaLabels.FOR_RENT_BY}
              value={MODAL_COMPONENT.ForRentByFieldSet.componentKey}
              onEditClick={onEditClick}
            />
          </StyledReviewTitleRow>

          <FieldValueDisplay value={FOR_RENT_BY[forRentBy]} customNoneText={customNoneFieldValueText} />
        </StyledReviewSection>
      )}

      {!isUpdateAgentRestricted && forRentBy === FOR_RENT_BY_KEY.AGENT && (
        <StyledReviewSection data-testid="AdditionalBrokerInformation">
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconPlaceholder />
            <Text textStyle="body-bold">Listing is exclusive</Text>
          </Box>

          <FieldValueDisplay value={displayListingExclusive} />
        </StyledReviewSection>
      )}

      {forRentBy === FOR_RENT_BY_KEY.AGENT && !isUpdateAgentRestricted && BROKER_LICENSE_MAP[agentLicenseType] && (
        <StyledReviewSection data-testid="AgentLicenseType">
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconPlaceholder />
            <Text textStyle="body-bold">License type</Text>
          </Box>

          <FieldValueDisplay value={BROKER_LICENSE_MAP[agentLicenseType]} />
        </StyledReviewSection>
      )}

      <StyledReviewSection data-testid="Name">
        <StyledReviewTitleRow>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconPlaceholder>
              {!contactName && !isDisabled && !isFeed && (
                <AccordionIconError tabIndex={0} title="Name incomplete error" marginRight="xs" />
              )}
              {recommendations[CONTACT][RECS.CONTACT] && (
                <RecommendationIconBulb
                  fieldName={ariaLabels.CONTACT_INFO_NAME}
                  removeFlex={true}
                  onClick={() => {
                    setRecommendationType(RECS.CONTACT);
                  }}
                />
              )}
            </StyledIconPlaceholder>
            <StyledReviewSectionTitle>
              <Text textStyle="body-bold">Name</Text>
              {recommendations[CONTACT][RECS.CONTACT] && (
                <TextButton
                  aria-label={`learn-more-about-${ariaLabels.CONTACT_INFO_NAME}`}
                  textStyle="body"
                  onClick={() => {
                    setRecommendationType(RECS.CONTACT);
                  }}
                >
                  Learn more
                </TextButton>
              )}
            </StyledReviewSectionTitle>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.CONTACT_INFO_NAME}
            value={MODAL_COMPONENT.NameFormField.componentKey}
            onEditClick={onEditClick}
          />
        </StyledReviewTitleRow>

        <FieldValueDisplay value={contactName} className="fs-mask" />
      </StyledReviewSection>

      <StyledReviewSection data-testid="Email">
        <StyledReviewTitleRow>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconPlaceholder>
              {!contactEmail && !isDisabled && (
                <AccordionIconError tabIndex={0} title="Email incomplete error" marginRight="xs" />
              )}
            </StyledIconPlaceholder>
            <Text textStyle="body-bold">Email</Text>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.CONTACT_INFO_EMAIL}
            value={MODAL_COMPONENT.EmailFormField.componentKey}
            onEditClick={onEditClick}
          />
        </StyledReviewTitleRow>

        <FieldValueDisplay value={contactEmail} className="fs-mask" />
        {contactEmail && !isDisabled && (
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconPlaceholder />
            <Box css={{ flex: 1 }}>
              <Box css={{ marginBlock: '400' }}>
                <ContactEmailMismatchAlert contactEmail={contactEmail} />
              </Box>

              {canViewContactEmailDeliverability && (
                <Box css={{ marginTop: '400' }}>
                  <ContactEmailDeliverabilityAlert />
                </Box>
              )}
            </Box>
          </Box>
        )}
      </StyledReviewSection>

      <StyledReviewSection data-testid="Phone">
        <StyledReviewTitleRow>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconPlaceholder>
              {!contactPhone && !isDisabled && (
                <AccordionIconError tabIndex={0} title="Phone incomplete error" marginRight="xs" />
              )}
            </StyledIconPlaceholder>
            <Text textStyle="body-bold">Phone</Text>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.CONTACT_INFO_PHONE}
            value={MODAL_COMPONENT.PhoneNumberFormField.componentKey}
            onEditClick={handleEditPhoneClick}
          />
        </StyledReviewTitleRow>

        <FieldValueDisplay value={displayContactPhone} className="fs-mask" />

        {displayContactPhone && !isDisabled && !isRoomForRent && (
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconPlaceholder />
            <Box css={{ flex: '1 0' }}>
              <Box css={{ marginTop: '400' }}>
                <PhoneNumberDisplayInfo />
              </Box>
            </Box>
          </Box>
        )}
      </StyledReviewSection>

      {forRentBy !== FOR_RENT_BY_KEY.AGENT && (
        <StyledReviewSection data-testid="AllowContactByPhone">
          <StyledReviewTitleRow>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <StyledIconPlaceholder>
                {recommendations[CONTACT][RECS.HIDE_PHONE] && (
                  <RecommendationIconBulb
                    fieldName={ariaLabels.CONTACT_BY_PHONE}
                    removeFlex={true}
                    onClick={() => {
                      setRecommendationType(RECS.HIDE_PHONE);
                    }}
                  />
                )}
              </StyledIconPlaceholder>
              <StyledReviewSectionTitle>
                <Text textStyle="body-bold">Allow renters to contact you by phone</Text>
                {!isFeed && (
                  <TextButton
                    aria-label={`learn-more-about-${ariaLabels.CONTACT_BY_PHONE}`}
                    textStyle="body"
                    onClick={() => {
                      setRecommendationType(RECS.HIDE_PHONE);
                    }}
                  >
                    Learn more
                  </TextButton>
                )}
              </StyledReviewSectionTitle>
            </Box>

            <ContentSectionEditButton
              buttonFieldName={ariaLabels.CONTACT_BY_PHONE}
              value={MODAL_COMPONENT.AllowContactByPhoneFieldSet.componentKey}
              onEditClick={onEditClick}
            />
          </StyledReviewTitleRow>

          <FieldValueDisplay value={ALLOW_CONTACT_BY_PHONE[String(!hidePhone)]} />
        </StyledReviewSection>
      )}

      {canAccessPostingPathTourScheduling && (
        <>
          <HR />
          <Box css={{ marginBottom: '1000' }} />

          <div data-eventid={TouringModularListingCSEvents.ReviewStepBookingMethodImpression.data_event_id}>
            <TouringSection
              onClickRecommendation={setRecommendationType}
              onClickEditTourSection={handleEditTourClick}
            />
          </div>
        </>
      )}

      {Boolean(!canAccessPostingPathTourScheduling && (!isFeed || !isLegacyTourDisabled)) && (
        <>
          <HR />
          <Box css={{ marginBottom: '1000' }} />

          <StyledReviewSection data-testid="TourAvailability">
            <StyledReviewTitleRow>
              <Box css={{ display: 'flex', alignItems: 'center' }}>
                <StyledIconPlaceholder>
                  {Boolean(!isLegacyTourDisabled && recommendations[CONTACT][RECS.SCHEDULE]) && (
                    <RecommendationIconBulb
                      fieldName={ariaLabels.TOUR_AVAILABILITY}
                      removeFlex={true}
                      onClick={() => {
                        setRecommendationType(RECS.SCHEDULE);
                      }}
                    />
                  )}
                </StyledIconPlaceholder>

                <StyledReviewSectionTitle>
                  <Text textStyle="body-bold">Tour availability</Text>

                  {Boolean(!isLegacyTourDisabled && !isFeed) && (
                    <TextButton
                      aria-label={`learn-more-about-${ariaLabels.TOUR_AVAILABILITY}`}
                      textStyle="body"
                      onClick={() => {
                        setRecommendationType(RECS.SCHEDULE);
                      }}
                    >
                      Learn more
                    </TextButton>
                  )}
                </StyledReviewSectionTitle>
              </Box>
            </StyledReviewTitleRow>

            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <StyledIconPlaceholder />

              <Box css={{ flexGrow: 1 }}>
                {!isLegacyTourDisabled ? (
                  <SchedulerFormField hideTitle={true} hideLabel={true} textStyle="body-bold" />
                ) : (
                  <Alert
                    actionButton={
                      active && (
                        <TextButton>
                          <a
                            href={`${paths.propertiesPage}/${listingAlias}/tours/tour-calendar`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View or update your availability
                          </a>
                        </TextButton>
                      )
                    }
                  >
                    {!active
                      ? "After you publish your listing, you'll be able to set your availability for tours."
                      : "Zillow's new tour scheduling feature lets you add your availability to a calendar for easier booking."}
                  </Alert>
                )}
              </Box>
            </Box>
          </StyledReviewSection>
        </>
      )}

      {isHousingConnector && canAccessHousingConnector && (
        <>
          <HR />
          <Box css={{ marginBottom: '1000' }} />

          <StyledReviewSection data-testid="HousingConnector">
            <StyledReviewTitleRow>
              <Box css={{ display: 'flex', alignItems: 'center' }}>
                <StyledIconPlaceholder />
                <Text textStyle="body-bold">Housing connector</Text>
              </Box>

              <ContentSectionEditButton
                buttonFieldName={ariaLabels.HOUSING_CONNECTOR}
                value={MODAL_COMPONENT.HousingConnectorFieldSet.componentKey}
                onEditClick={onEditClick}
              />
            </StyledReviewTitleRow>

            <FieldValueDisplay value={HOUSING_CONNECTOR_NETWORK_OPTIONS[housingConnectorStatus]} />
          </StyledReviewSection>
        </>
      )}

      {isApplicationsAccessRestricted === false && (
        <>
          <HR />
          <Box css={{ marginBottom: '1000' }} />

          <StyledReviewSection data-testid="ZillowApplications">
            <StyledReviewTitleRow>
              <Box css={{ display: 'flex', alignItems: 'center' }}>
                <StyledIconPlaceholder>
                  {!active &&
                  !isApplicationsUpdateRestricted &&
                  (isApplicationsAccepted === null ||
                    (!isApplicationsAccepted && !typeCheckUtils.isNumeric(feeAmount))) &&
                  !isDisabled ? (
                    <AccordionIconError tabIndex={0} title="Applications incomplete error" marginRight="xs" />
                  ) : recommendations[CONTACT][RECS.APPLICATIONS] ? (
                    <RecommendationIconBulb
                      fieldName={ariaLabels.ZILLOW_APPLICATIONS}
                      removeFlex={true}
                      onClick={() => {
                        setRecommendationType(RECS.APPLICATIONS);
                      }}
                    />
                  ) : null}
                </StyledIconPlaceholder>

                <StyledReviewSectionTitle>
                  <Text textStyle="body-bold">Accept Zillow online applications</Text>

                  <Box css={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', height: '600' }}>
                    <Tag tone="info" css={{ marginRight: '200' }}>
                      Free
                    </Tag>

                    <TextButton
                      aria-label={`see-sample-${ariaLabels.ZILLOW_APPLICATIONS}`}
                      textStyle="body"
                      onClick={() => {
                        setDisplaySampleModal(true);
                        ApplicationsCSEvents.ClickSampleApplicationLinkOnPage({ currentProperty, user });
                      }}
                      data-eventid={ApplicationsCSEvents.ClickSampleApplicationLinkOnPage.data_event_id}
                    >
                      Sample
                    </TextButton>
                  </Box>
                </StyledReviewSectionTitle>
              </Box>

              <ContentSectionEditButton
                buttonFieldName={ariaLabels.ZILLOW_APPLICATIONS}
                value={MODAL_COMPONENT.AcceptZillowApplicationsFieldSet.componentKey}
                onEditClick={onEditClick}
                allowFeedEdit={true}
                isDisabled={isApplicationsUpdateRestricted}
                disabledMessage={
                  applicationsOwnerEmail
                    ? `To make changes, please log in as ${applicationsOwnerEmail}.`
                    : `To make changes, navigate to your Property Overview page, then select the Applications tab.`
                }
              />
            </StyledReviewTitleRow>

            <FieldValueDisplay
              value={RECEIVE_ZILLOW_APPLICATIONS[(isApplicationsAccepted as boolean)?.toString()]}
              customNoneText={!isApplicationsUpdateRestricted ? customNoneFieldValueText : NONE}
            />

            {canAccessSFCostsAndFees && feeAmount !== null && !isApplicationsAccepted && (
              <Box css={{ marginTop: '400' }}>
                <FieldValueDisplay
                  value={`Application fee: ${numberUtils.asCurrency(feeAmount, feeAmount % 1 != 0 ? '$0,0.00' : '$0,0')}`}
                  customNoneText=""
                  showIconPlaceholder={true}
                />
              </Box>
            )}
          </StyledReviewSection>
        </>
      )}

      {showPhoneVerificationModal && (
        <VerificationModal
          headerText="Add and verify phone number"
          onContentValueAdded={handlePhoneNumberAdded}
          onVerificationConfirmation={handleVerificationConfirmation}
          showVerificationModal={setShowPhoneVerificationModal}
          stepsConfig={DEFAULT_VERIFICATION_CONFIG}
        />
      )}
      {recommendationType && (
        <RecommendationsLearnMoreContent
          onClose={() => setRecommendationType('')}
          recommendationType={recommendationType}
        />
      )}
      {displaySampleModal && <ApplicationSampleModal onClose={() => setDisplaySampleModal(false)} />}
      {displayTourBookingModal && (
        <EditListingTourConfigModal
          step={touringModalStep}
          onStepChange={handleTouringModalStepChange}
          tourCalendarHook={tourCalendarHook}
          onClose={() => setDisplayTourBookingModal(false)}
          isInRelistingFlow={canAccessRelistingFlow}
          onTourDurationSave={handleTourDurationSave}
          onTourDurationCancel={handleTourDurationCancel}
          canAccessPostingPathTourDuration={accessUtils.canAccessPostingPathTourDuration({ appConfig })}
        />
      )}
    </div>
  );
};

export default ContactContent;
